import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { isDisabledStatus } from '@/features/cards/hooks/usePaymentCardStatus';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { getCurrentCardTransactions, getDigitizedCards } from '@/store/actions/profile.actions';

import { selectCurrentProfileCardByUserId, selectProfileCardsByUserId } from '@/store/selectors';

import { useProfileContext } from '@/features/context/ProfileContext';
import { NotActivatedCard } from '@/features/cards/components/NotActivatedCard';
import { CardTransactionsTable } from '@/features/cards/components/CardTransactionsTable/CardTransactionsTable';
import { CardTransactionSimulation } from '@/features/cards/components/CardTransactionSimulation';
import { ColumnsProvider } from '@/features/cards/context/CardTransactionColumnContext';
import { mandatoryColumnConfig } from '@/features/cards/store/models/card-transactions.model';

export function CardTransactions() {
  const { t } = useTranslation();
  const { user } = useProfileContext();
  const dispatch = useAppDispatch();
  const dashboardConfig = useDashboardConfig();

  const cardTransactions = useAppSelector((state) => selectCurrentProfileCardByUserId(state, user.userId))?.state
    ?.paginateCardTransactions?.state?.elements;

  const digitizedCards = useAppSelector(
    (state) => selectCurrentProfileCardByUserId(state, user.userId)?.state?.digitizedCards?.state,
  );

  const cursor = useAppSelector((state) => selectCurrentProfileCardByUserId(state, user.userId))?.state
    ?.paginateCardTransactions?.state?.cursor;

  const allCards = useAppSelector((state) => selectProfileCardsByUserId(state, user.userId))?.state?.map(
    (cards) => cards.state?.card,
  );

  const currentCard = useAppSelector((state) => selectCurrentProfileCardByUserId(state, user.userId))?.state?.card;
  const currentCardIndex = allCards.findIndex((card) => card.cardId === currentCard?.cardId);
  const totalRows = allCards?.[0]?.totalRows;

  const isTransactionsDataLoading = useAppSelector((state) => selectCurrentProfileCardByUserId(state, user.userId))
    ?.state?.paginateCardTransactions?.isLoading;

  const isCurrentCardLoading = useMemo(
    () => isTransactionsDataLoading || !currentCard?.userId,
    [currentCard?.userId, isTransactionsDataLoading],
  );

  useEffect(() => {
    if (currentCard?.cardId) {
      dispatch(getCurrentCardTransactions(currentCard.cardId, user.userId));
      if (!!dashboardConfig?.settings.cards.digitizedCards) {
        dispatch(getDigitizedCards(currentCard.cardId, user.userId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard?.cardId, dispatch, user.userId]);

  const handleChangeFilter = useCallback(
    ({ filters, cursorId }) => {
      if (currentCard) dispatch(getCurrentCardTransactions(currentCard.cardId, user.userId, filters, cursorId));
    },
    [dispatch, currentCard, user.userId],
  );

  if (!currentCard) return <></>;

  return (
    <div className="flex flex-col flex-1 gap-4 overflow-auto">
      <div className="flex items-start justify-between">
        <div className="flex flex-col justify-start gap-1">
          <h5 className="typo-h5-bold text-secondary">
            {t('pages.currentProfiles.cards.card.transactionTableHeader')}
          </h5>
          {currentCardIndex !== -1 && Number(totalRows) > 0 && (
            <span className="typo-body-regular-small text-tertiary">
              {t('pages.currentProfiles.cards.card.cardIndex', { index: currentCardIndex + 1, total: totalRows })}
            </span>
          )}
        </div>
        <CardTransactionSimulation card={currentCard} />
      </div>
      <div>
        {currentCard && !currentCard?.isLive && !isDisabledStatus(currentCard.statusCode) ? (
          <NotActivatedCard paymentCard={currentCard} />
        ) : (
          <ColumnsProvider initialConfig={mandatoryColumnConfig}>
            <CardTransactionsTable
              cardTransactions={cardTransactions}
              cursor={cursor}
              onFiltersChange={handleChangeFilter}
              isLoadingTransactions={!!isCurrentCardLoading}
              digitizedCards={digitizedCards}
              className="h-[calc(100vh-450px)]"
            />
          </ColumnsProvider>
        )}
      </div>
    </div>
  );
}
